/**
 * 재동의 배너 (#372 / admin#1361)
 *
 * 정책 버전 변경 등으로 재동의가 필요한 사용자에게 강제 모달을 표시한다.
 * - Primary 동의 거부: 로그아웃 + Aviso 페이지 (서비스 차단)
 * - Optional marketing: authenticated notice review with explicit grant/withdraw or skip.
 *
 * 모든 인증 필요 페이지에서 표시되도록 App 루트 또는 layout 에 통합한다.
 *
 * @see admin#1361 (메타) / admin#1365 (BE)
 * @see #372 (FE 하위)
 */
import { useLayoutEffect, useMemo, useRef, useState, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/AppContext';
import { consentApi } from '@/lib/api/consents';
import { apiClient } from '@/lib/api/client';
import { MarketingConsentReview } from '@/components/privacy/MarketingConsentReview';
import type { Language } from '@/config/locales';
import { logger } from '@/utils/logger';

interface ReConsentBannerProps {
  /** Aviso 정책 페이지 표시 콜백 (menu#346 정합) */
  onShowAviso?: () => void;
}

interface BannerLabels {
  title: string;
  description: string;
  primary: string;
  avisoLink: string;
  confirm: string;
  reject: string;
  rejectConfirm: string;
}

function getLabels(language: Language): BannerLabels {
  if (language === 'ko') {
    return {
      title: '개인정보 처리 방침이 업데이트되었습니다',
      description: '서비스 계속 이용을 위해 동의 내역을 다시 확인해 주세요.',
      primary: '서비스 운영 / 결제 처리 / ARCO 권리 (필수)',
      avisoLink: '개인정보 고지 보기',
      confirm: '동의하고 계속',
      reject: '동의하지 않음',
      rejectConfirm: '필수 동의를 거부하면 로그아웃됩니다. 계속하시겠습니까?',
    };
  }
  if (language === 'es') {
    return {
      title: 'Hemos actualizado nuestra política de privacidad',
      description: 'Por favor revisa nuevamente tu consentimiento para continuar usando el servicio.',
      primary: 'Operación del servicio / Pagos / Derechos ARCO (Obligatorio)',
      avisoLink: 'Ver Aviso de Privacidad',
      confirm: 'Aceptar y continuar',
      reject: 'No acepto',
      rejectConfirm: 'Si rechazas el consentimiento obligatorio, se cerrará tu sesión. ¿Deseas continuar?',
    };
  }
  return {
    title: 'Our privacy policy has been updated',
    description: 'Please review your consent again to continue using the service.',
    primary: 'Service operation / Payments / ARCO rights (Required)',
    avisoLink: 'View Privacy Notice',
    confirm: 'Accept and continue',
    reject: 'Decline',
    rejectConfirm: 'Declining required consent will log you out. Continue?',
  };
}

export function ReConsentBanner({ onShowAviso }: ReConsentBannerProps) {
  const { isLoggedIn, customerId, user, logout } = useAuth();
  const { language, locale } = useLanguage();
  const [needsReconsent, setNeedsReconsent] = useState<boolean>(false);
  const [primary, setPrimary] = useState<boolean>(false);
  const [primaryRecorded, setPrimaryRecorded] = useState(false);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const identityEpoch = apiClient.getAuthIdentityEpoch();
  const scope = useMemo(() => ({ isLoggedIn, customerId: user?.id ?? customerId, identityEpoch }), [isLoggedIn, customerId, user?.id, identityEpoch]);
  const activeScopeRef = useRef<typeof scope | null>(null);
  useLayoutEffect(() => {
    const controller = new AbortController();
    activeScopeRef.current = scope;
    setNeedsReconsent(false);
    setPrimary(false);
    setPrimaryRecorded(false);
    setSubmitting(false);
    setError(null);
    const active = () => activeScopeRef.current === scope && scope.identityEpoch === apiClient.getAuthIdentityEpoch() && !controller.signal.aborted;
    if (scope.isLoggedIn) {
      void (async () => {
        try {
          const res = await consentApi.getStatus(controller.signal);
          if (!active()) return;
          setNeedsReconsent(Boolean(res.data?.needs_reconsent));
        } catch (err) {
          if (!active()) return;
          logger.warn('동의 상태 조회 실패 (#372)', err);
        }
      })();
    }
    return () => {
      controller.abort();
      if (activeScopeRef.current === scope) activeScopeRef.current = null;
    };
  }, [scope]);

  const labels = getLabels(language);
  const ownsScope = useCallback(() => activeScopeRef.current === scope && scope.identityEpoch === apiClient.getAuthIdentityEpoch(), [scope]);

  const handleConfirm = useCallback(async () => {
    if (!ownsScope() || !scope.isLoggedIn) return;
    if (!primary) {
      setError(labels.primary);
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      // #424: BE 계약 정합 — status (enum) + via (in) obligatorio. granted boolean 폐기.
      // Primary 재동의 갱신 (배너 채널 = 'banner')
      await consentApi.record({ category: 'primary', status: 'granted', via: 'banner' });
      if (!ownsScope()) return;
      setPrimaryRecorded(true);
    } catch (err) {
      if (!ownsScope()) return;
      logger.error('재동의 갱신 실패 (#372)', err);
      setError(String(err instanceof Error ? err.message : err));
    } finally {
      if (ownsScope()) setSubmitting(false);
    }
  }, [primary, labels.primary, scope, ownsScope]);

  const handleReject = useCallback(async () => {
    if (!ownsScope()) return;
    // Primary 거부 = 서비스 차단 → 로그아웃
    const ok = window.confirm(labels.rejectConfirm);
    if (!ok) return;
    // clearSession advances identity once. A later login/account change must not
    // receive this old denial's navigation; normal logout may complete after render.
    const ownsCompletion = () => (activeScopeRef.current === scope && apiClient.getAuthIdentityEpoch() === scope.identityEpoch) || (
      activeScopeRef.current !== null && !activeScopeRef.current.isLoggedIn &&
      apiClient.getAuthIdentityEpoch() === scope.identityEpoch + 1
    );
    try {
      await logout();
    } catch (err) {
      if (!ownsCompletion()) return;
      logger.warn('재동의 거부 후 로그아웃 실패 (#372)', err);
    }
    if (!ownsCompletion()) return;
    setNeedsReconsent(false);
    onShowAviso?.();
  }, [logout, labels.rejectConfirm, onShowAviso, scope, ownsScope]);

  if (!isLoggedIn || !needsReconsent) {
    return null;
  }

  if (primaryRecorded) {
    return <div role="dialog" aria-modal="true" aria-label={labels.title} data-testid="reconsent-banner" className="fixed inset-0 z-[9999] overflow-y-auto bg-background">
      <MarketingConsentReview
        key={`${scope.identityEpoch}:${scope.customerId}`}
        customerKey={scope.customerId ?? null}
        locale={locale ?? language}
        isCurrent={() => activeScopeRef.current === scope && scope.identityEpoch === apiClient.getAuthIdentityEpoch()}
        onContinue={() => setNeedsReconsent(false)}
      />
    </div>;
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      data-testid="reconsent-banner"
      className="fixed inset-0 z-[9999] flex items-end sm:items-center justify-center bg-black/50 p-4"
    >
      <div className="w-full max-w-md rounded-t-2xl sm:rounded-2xl bg-background p-6 shadow-2xl">
        <h2 className="text-lg font-bold text-foreground mb-2">{labels.title}</h2>
        <p className="text-sm text-muted-foreground mb-4">{labels.description}</p>

        {onShowAviso && (
          <button
            type="button"
            onClick={onShowAviso}
            className="text-primary hover:text-primary-hover underline text-[13px] mb-4"
          >
            {labels.avisoLink}
          </button>
        )}

        <label className="flex items-start gap-3 mb-3 cursor-pointer">
          <input
            type="checkbox"
            data-testid="reconsent-primary-checkbox"
            checked={primary}
            onChange={(e) => setPrimary(e.target.checked)}
            className="mt-1"
          />
          <span className="text-sm text-foreground">{labels.primary}</span>
        </label>


        {error && (
          <p
            data-testid="reconsent-error"
            className="text-sm text-destructive mb-3"
          >
            {error}
          </p>
        )}

        <div className="flex flex-col sm:flex-row gap-2">
          <button
            type="button"
            data-testid="reconsent-confirm"
            onClick={handleConfirm}
            disabled={submitting}
            className="flex-1 bg-primary text-primary-foreground rounded-lg py-3 font-medium disabled:opacity-50"
          >
            {labels.confirm}
          </button>
          <button
            type="button"
            data-testid="reconsent-reject"
            onClick={handleReject}
            disabled={submitting}
            className="flex-1 bg-muted text-foreground rounded-lg py-3 font-medium disabled:opacity-50"
          >
            {labels.reject}
          </button>
        </div>
      </div>
    </div>
  );
}
